import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X, FileText, Download, Phone, AlertTriangle, ExternalLink, Info } from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import { PageHero } from "./Services";
import { brand, heroImage, manufacturerWarranties, warranty } from "../data/site";

const TERM = warranty.laborYears || "25";

// "A, B and C" rather than join(" and "), which reads badly past two.
const nameList = (names) =>
  names.length < 3
    ? names.join(" and ")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

// The three-warranty framing. A homeowner who understands this stops asking
// the wrong company to pay for things, which is most of what goes wrong when
// a roof has a problem.
const THREE = [
  {
    who: "The manufacturer",
    what: "Covers the products themselves — defects in the shingle, the underlayment, the ridge cap. Breakdowns for each manufacturer are below.",
  },
  {
    who: "Pepla",
    what: `Covers how the roof was installed. ${TERM} years, non-prorated, no deductible, and it transfers once to the next owner for free.`,
    link: { to: "/warranty-claim", label: "Our workmanship warranty" },
  },
  {
    who: "Your insurance",
    what: "Covers what the weather does — hail, a microburst that strips shingles, a tree through the roof. No manufacturer warranty covers storm damage.",
    link: { to: "/insurance-claims", label: "Storm & insurance help" },
  },
];

const jsonLd = (list) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "FAQPage",
      mainEntity: list.flatMap((m) => [
        {
          "@type": "Question",
          name: `What does the ${m.name} roofing warranty cover?`,
          acceptedAnswer: { "@type": "Answer", text: m.covers.join(". ") + "." },
        },
        {
          "@type": "Question",
          name: `What is not covered by the ${m.name} warranty?`,
          acceptedAnswer: { "@type": "Answer", text: m.excludes.join(". ") + "." },
        },
        {
          "@type": "Question",
          name: `Does the ${m.name} warranty transfer if I sell my house?`,
          acceptedAnswer: { "@type": "Answer", text: m.transfer },
        },
      ]),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Understanding Your Warranty", item: `${SITE_URL}/understanding-your-warranty` },
      ],
    },
  ],
});

// The badge opens the manufacturer's own PDF. Clicking the picture is what
// people try first, so the picture is the link rather than a caption under it.
const BadgeLink = ({ m }) => (
  <a
    href={m.pdf}
    target="_blank"
    rel="noopener noreferrer"
    className="group block rounded-2xl border border-ink/10 bg-card p-6 transition-colors hover:border-brand focus:outline-none focus:ring-2 focus:ring-brand"
    aria-label={`Open the ${m.title} (PDF, ${m.pdfSize}, opens in a new tab)`}
  >
    {/* A manufacturer with a warranty seal shows the seal. One without —
        TAMKO's SA membrane has none — shows its mark instead, boxed to the
        same height so the three cards line up. */}
    {m.badge ? (
      <picture>
        <source srcSet={`${m.badge}.webp`} type="image/webp" />
        <img
          src={`${m.badge}.jpg`}
          alt={m.badgeAlt}
          width={480}
          height={480}
          loading="lazy"
          decoding="async"
          className="w-full max-w-[220px] mx-auto h-auto"
        />
      </picture>
    ) : (
      <span className="flex h-[220px] items-center justify-center px-4">
        <img
          src={m.logo}
          alt={m.logoAlt}
          loading="lazy"
          decoding="async"
          className="w-full max-w-[180px] h-auto"
        />
      </span>
    )}
    <span className="mt-5 flex items-center justify-center gap-2 text-brand-dark font-semibold uppercase tracking-wider text-xs group-hover:gap-3 transition-all">
      <FileText size={15} aria-hidden="true" /> Read the full warranty
    </span>
    <span className="mt-1.5 block text-center text-[11px] text-ink/45">
      PDF · {m.pdfSize} · opens in a new tab
    </span>
  </a>
);

const WarrantyGuide = () => (
  <div>
    <SEO
      title="Understanding Your Roof Warranty | Pepla Roofing"
      // Built from the list so it cannot go stale the next time a
      // manufacturer is added to src/data/site.js.
      description={`What the ${nameList(
        manufacturerWarranties.map((m) => m.name)
      )} roofing warranties actually cover, what they exclude, and what you have to do to qualify — in plain English, with each manufacturer's own document attached.`}
      jsonLd={jsonLd(manufacturerWarranties)}
    />

    <PageHero
      eyebrow="Know What You Have"
      title="Understanding Your Warranty"
      subtitle="Three different warranties end up on your roof and they cover three different things. Here is what each one actually says."
      image={heroImage}
      imageAlt="Arizona roof under warranty"
    />

    {/* Three warranties */}
    <section className="py-24 bg-background">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">Three Promises, Three Companies</h2>
        <div className="brand-line mb-5" />
        <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl mb-10">
          Most warranty arguments in this industry come from one confusion: a homeowner asks the wrong
          company to pay for something. These three never overlap.
        </p>

        <div className="rounded-2xl overflow-hidden border border-ink/10 bg-ink/10">
          <div className="grid md:grid-cols-3 gap-px">
            {THREE.map((t) => (
              <div key={t.who} className="bg-card p-7 sm:p-8 flex flex-col">
                <h3 className="font-heading text-xl uppercase tracking-wide mb-3">{t.who}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-5">{t.what}</p>
                {t.link && (
                  <Link
                    to={t.link.to}
                    className="mt-auto inline-flex items-center gap-2 text-brand-dark font-semibold uppercase tracking-wider text-xs link-underline self-start"
                  >
                    {t.link.label} <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Jump links. The page is one long document and most people arrive
            knowing which shingle is on their roof. Hidden when there is only
            one manufacturer, since a menu of one is just clutter. */}
        {manufacturerWarranties.length > 1 && (
          <nav aria-label="Manufacturer warranties" className="mt-10 flex flex-wrap items-center gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/45">
              Jump to
            </span>
            {manufacturerWarranties.map((m) => (
              <a
                key={m.slug}
                href={`#${m.slug}`}
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-ink hover:border-brand hover:text-brand-dark transition-colors"
              >
                {m.name} <ArrowRight size={13} aria-hidden="true" />
              </a>
            ))}
          </nav>
        )}
      </div>
    </section>

    {manufacturerWarranties.map((m, i) => (
      <section
        key={m.slug}
        id={m.slug}
        className={`py-24 scroll-mt-28 ${i % 2 === 0 ? "bg-secondary" : "bg-background"}`}
      >
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <span className="kicker">Manufacturer warranty</span>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">{m.title}</h2>
          <div className="brand-line mb-6" />

          <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-10 lg:gap-14 items-start">
            <div>
              <p className="text-muted-foreground text-lg leading-relaxed mb-4">{m.lede}</p>
              <p className="text-ink/45 text-xs">{m.effective}</p>
            </div>
            <BadgeLink m={m} />
          </div>

          {/* The numbers */}
          <div className="mt-14 rounded-2xl overflow-hidden border border-ink/10 bg-ink/10">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px">
              {m.terms.map((t) => (
                <div key={t.label} className="bg-card p-6 sm:p-7">
                  <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/45 mb-2">
                    {t.label}
                  </span>
                  <span className="block font-heading text-2xl uppercase tracking-wide text-ink mb-3">
                    {t.value}
                  </span>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t.note}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Covered / not covered. items-start so each card takes its own
              height — the TAMKO membrane warranty covers three things and
              excludes twelve, and stretching the short card to match left a
              block of empty white that read as a layout fault rather than as
              the point. */}
          <div className="mt-10 grid md:grid-cols-2 gap-6 items-start">
            <div className="bg-card border border-ink/10 rounded-2xl p-7 sm:p-8">
              <h3 className="font-heading text-xl uppercase tracking-wide mb-5">What it covers</h3>
              <ul className="space-y-3">
                {m.covers.map((c) => (
                  <li key={c} className="flex gap-3 text-muted-foreground text-sm leading-relaxed">
                    <Check size={16} className="text-brand-dark shrink-0 mt-0.5" aria-hidden="true" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-ink text-white rounded-2xl p-7 sm:p-8">
              <h3 className="font-heading text-xl uppercase tracking-wide mb-5">What it does not</h3>
              <ul className="space-y-3">
                {m.excludes.map((c) => (
                  <li key={c} className="flex gap-3 text-white/70 text-sm leading-relaxed">
                    <X size={16} className="text-brand shrink-0 mt-0.5" aria-hidden="true" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Eligibility — the part nobody mentions at the kitchen table */}
          <div className="mt-10 bg-card border-l-4 border-brand rounded-r-2xl rounded-l-sm p-7 sm:p-8">
            <h3 className="font-heading text-xl uppercase tracking-wide mb-3">
              You only get this warranty if the roof was built for it
            </h3>
            <p className="text-muted-foreground leading-relaxed mb-5">
              This is the part that gets skipped at the kitchen table. The coverage above exists only when
              the system was installed to {m.name}'s eligibility rules. Install the shingle on its own and
              you fall back to a much thinner warranty.
            </p>
            <ul className="space-y-3 mb-6">
              {m.qualify.map((q) => (
                <li key={q} className="flex gap-3 text-muted-foreground text-sm leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0 mt-2" aria-hidden="true" />
                  {q}
                </li>
              ))}
            </ul>
            <p className="text-ink/70 text-sm leading-relaxed">
              Every product on a Pepla estimate is named by manufacturer, line and color, so you can check
              this against the eligibility list before you sign rather than after a claim is denied.
            </p>
          </div>

          {/* Transfer + claims */}
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="bg-card border border-ink/10 rounded-2xl p-7 sm:p-8">
              <h3 className="font-heading text-xl uppercase tracking-wide mb-3">If you sell the house</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{m.transfer}</p>
            </div>
            <div className="bg-card border border-ink/10 rounded-2xl p-7 sm:p-8">
              <h3 className="font-heading text-xl uppercase tracking-wide mb-3">Making a claim</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                <b className="text-ink">Within {m.claim.window}.</b> {m.claim.note}
              </p>
              <ul className="space-y-1.5">
                {m.claim.routes.map((r) => (
                  <li key={r} className="text-ink/70 text-sm tabular-nums">{r}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Side note — the manufacturer's own wider context, where there
              is one. Deliberately quiet: it is a footnote, not a CTA. */}
          {m.sideNote && (
            <aside className="mt-10 rounded-2xl border border-ink/15 bg-secondary px-7 py-7 sm:px-8">
              <div className="flex gap-4">
                <Info size={19} className="text-brand-dark shrink-0 mt-0.5" aria-hidden="true" />
                <div className="min-w-0">
                  <h3 className="font-heading text-lg uppercase tracking-wide mb-2">
                    {m.sideNote.heading}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    {m.sideNote.body}
                  </p>
                  {/* A side note carries either one link (linkLabel/linkUrl/
                      linkNote) or several (links[]). Normalized here so both
                      shapes render the same and neither has to be migrated. */}
                  <div className="space-y-3">
                    {(m.sideNote.links || [
                      { label: m.sideNote.linkLabel, url: m.sideNote.linkUrl, note: m.sideNote.linkNote },
                    ]).map((l) => (
                      <div key={l.url}>
                        <a
                          href={l.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-brand-dark font-semibold uppercase tracking-wider text-xs link-underline"
                        >
                          {l.label} <ExternalLink size={13} aria-hidden="true" />
                        </a>
                        <span className="block text-[11px] text-ink/45 mt-1.5">{l.note}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          )}

          {/* Local reality */}
          <div className="mt-10">
            <h3 className="font-heading text-2xl uppercase tracking-wide mb-6">
              What this means on a Phoenix roof
            </h3>
            <div className="rounded-2xl overflow-hidden border border-ink/10 bg-ink/10">
              <div className="grid sm:grid-cols-2 gap-px">
                {m.local.map((l) => (
                  <div key={l.h} className="bg-card p-7 sm:p-8">
                    <h4 className="font-heading text-lg uppercase tracking-wide mb-2">{l.h}</h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">{l.p}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <a
            href={m.pdf}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-10 inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-7 py-4 rounded-md transition-colors"
          >
            <Download size={17} aria-hidden="true" /> Download the {m.name} warranty
          </a>
        </div>
      </section>
    ))}

    {/* Everything else we install. Deliberately not a fourth breakdown —
        we do not host documents for products we have not specced, and a
        thin section would undercut the three that are real. */}
    <section
      className={`py-20 ${
        // Keeps the alternation going from the last manufacturer section, so
        // adding a fourth never leaves two identical bands touching.
        manufacturerWarranties.length % 2 === 0 ? "bg-secondary" : "bg-background"
      }`}
    >
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mb-4">
          Another Brand On Your Roof?
        </h2>
        <div className="brand-line mb-6" />
        <p className="text-muted-foreground leading-relaxed mb-5">
          These three are broken down here because they are the manufacturers we most often
          put on Valley roofs. They are not the only ones we install — CertainTeed, IKO, Atlas,
          Eagle, Westlake and Brava all turn up on Phoenix homes, and every one of them writes
          its warranty differently.
        </p>
        <p className="text-muted-foreground leading-relaxed mb-8">
          If yours is not on this page, ask. We will pull the exact document for the product on
          your roof — the version in force on the day it was installed, which is the one that
          governs a claim — and walk you through it the same way. No charge, and you do not have
          to be a customer.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-7 py-4 rounded-md transition-colors"
          >
            Ask about your warranty <ArrowRight size={16} />
          </Link>
          <a
            href={`tel:${brand.phoneRaw}`}
            className="inline-flex items-center justify-center gap-2 border border-ink/20 text-ink font-semibold uppercase tracking-wider text-sm px-7 py-4 rounded-md hover:border-brand transition-colors"
          >
            <Phone size={16} /> {brand.phone}
          </a>
        </div>
      </div>
    </section>

    {/* Honest caveat + CTA */}
    <section className="py-20 section-dark text-white">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <div className="flex gap-4 mb-8">
          <AlertTriangle size={22} className="text-brand shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-white/70 leading-relaxed text-sm">
            These summaries are ours, written to be read. The manufacturer's document is the one that
            governs a claim, it is attached above, and the version in force is the one current on the day
            your roof was installed. Where our summary and the document disagree, the document wins.
          </p>
        </div>
        <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mb-4">
          Not Sure What You Have?
        </h2>
        <p className="text-white/70 leading-relaxed mb-8">
          Send us the paperwork from your last roof, or let us get up there and look. We will tell you
          which warranty you actually hold and whether the roof was built to qualify for it. Free either way.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
          >
            Book a free inspection <ArrowRight size={17} />
          </Link>
          <a
            href={`tel:${brand.phoneRaw}`}
            className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md hover:bg-white/10 transition-colors"
          >
            <Phone size={16} /> {brand.phone}
          </a>
        </div>
      </div>
    </section>
  </div>
);

export default WarrantyGuide;
